import {
  AutocompleteV2,
  AutocompleteV2Props,
  Box,
  Stack,
} from '@cast/design-system';
import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';

import { useNavigate } from '@cast/infrastructure-organization';
import { DboDatabase } from '@cast/types-database-optimizer';
import { AxiosError } from 'axios';

import { useDboParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { useInstanceDetails } from 'features/db-optimization/v2/hooks/useInstanceDetails';
import { SidebarSection } from 'main-layout-op/_components/sidebar/SecondarySidebar/_components/SidebarSection';
import { useVisibleDboNavItems } from 'pages/db-optimization/v2/DbOptimizationControllerV2/utils';
import { DboSidebarItem } from './DboSidebarItem';

type DatabaseNavItemsProps = {
  organizationId: string;
  isLoadingInstance: boolean;
  instanceError: AxiosError<unknown, any> | null;
};

export const DatabaseNavItems = ({
  organizationId,
  instanceError,
  isLoadingInstance,
}: DatabaseNavItemsProps) => {
  const navigate = useNavigate();
  const { dbServerId, databaseId } = useDboParams();
  const { isFetching, data, error } = useInstanceDetails();

  const dbParams = dbServerId && databaseId ? { dbServerId, databaseId } : null;

  const handleDatabaseChange: AutocompleteV2Props<DboDatabase>['onChange'] = ({
    value: newDatabaseId,
  }) => {
    if (!dbServerId || !newDatabaseId) return;
    navigate(
      buildDboRoute(DboRoute.DB_DASHBOARD, {
        dbServerId,
        databaseId: newDatabaseId,
      })
    );
  };

  const noDatabases = data?.databases?.length === 0;

  const visibleItems = useVisibleDboNavItems('database', {
    isOptimized: true,
    isManualProvider: data?.provider === 'MANUAL',
  });

  return (
    <SidebarSection title="Database">
      <Box mb={8}>
        <AutocompleteV2
          size="small"
          placeholder={
            isLoadingInstance || isFetching
              ? 'Loading...'
              : error || instanceError
              ? 'Error loading databases'
              : noDatabases
              ? 'No databases available'
              : 'Select database'
          }
          items={data?.databases || []}
          getLabel={({ name, id }) => name ?? id ?? ''}
          getStringValue={({ id }) => id ?? ''}
          value={databaseId}
          onChange={handleDatabaseChange}
          disabled={noDatabases}
        />
      </Box>

      {dbParams && (
        <Stack gap="4px">
          {visibleItems.map((item) => (
            <DboSidebarItem
              key={`${item.title}-${item.route ?? ''}`}
              params={dbParams}
              item={item}
              organizationId={organizationId}
            />
          ))}
        </Stack>
      )}
    </SidebarSection>
  );
};
